import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Divider,
  Paper,
  Skeleton,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ReactApexChart from "react-apexcharts";
import styles from "./intelliscore.module.scss";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import lightlogo from "../../../assets/intelli_score_lite.svg";
import darklogo from "../../../assets/Intelli_score_dark.svg";
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
// import Popover from "@mui/material/Popover";
import eventEmitter from "../../../components/utils/eventEmitter";
import SmallIntelliScoreWidget from "../../../components/SmallIntelliScoreWidget";
// import { useStorageManager } from "../../../shared/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { toast } from "react-toastify";
// import { set } from "lodash";

interface ScoreProps {
  value: number;
  label: React.ReactNode;
  color: string;
  assets: number | React.ReactNode;
  trend: "up" | "down" | "neutral";
  trendValue?: number;
  subLabel?: React.ReactNode;
}

const MainProgressGauge: React.FC<{
  score: number;
  isIPadEnhanced?: boolean;
  isIPadPro?: boolean;
}> = ({ score, isIPadEnhanced = false, isIPadPro = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const getScoreLabel = (score: number): string => {
    if (score >= 80) return "High";
    if (score >= 40) return "Medium";
    if (score > 0) return "Low";
    return "";
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return "#4CAF50"; // Green for high
    if (score >= 40) return "#FF9800"; // Orange for medium
    return "#F44336"; // Red for low
  };

  // Calculate the arc path for the score
  const calculateArc = (percentage: number) => {
    const radius = 58; // Match the new circle radius
    const centerX = 79;
    const centerY = 75;

    // If percentage is 0, return empty path
    if (percentage === 0) return "";

    // Special case: if percentage is 100, draw a full circle
    if (percentage >= 100) {
      // Draw a full circle using two arcs
      const startAngle = 90;
      const startRad = (startAngle * Math.PI) / 180;
      const startX = centerX + radius * Math.cos(startRad);
      const startY = centerY + radius * Math.sin(startRad);
      // Draw two arcs to make a full circle
      return `
        M ${startX} ${startY}
        A ${radius} ${radius} 0 1 1 ${centerX - radius * Math.cos(startRad)} ${centerY - radius * Math.sin(startRad)
        }
        A ${radius} ${radius} 0 1 1 ${startX} ${startY}
      `;
    }

    // Convert percentage to angle (0-360 degrees), starting from bottom going counter-clockwise
    const startAngle = 90; // Start from bottom
    const endAngle = startAngle + (percentage / 100) * 360; // Add to go counter-clockwise from bottom

    // Convert angles to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    // Calculate start and end points
    const startX = centerX + radius * Math.cos(startRad);
    const startY = centerY + radius * Math.sin(startRad);
    const endX = centerX + radius * Math.cos(endRad);
    const endY = centerY + radius * Math.sin(endRad);

    // Create arc flag (0 for angles ≤ 180°, 1 for angles > 180°)
    const arcFlag = percentage > 50 ? 1 : 0;

    // Create sweep flag (1 for counter-clockwise)
    const sweepFlag = 1;

    // Create the SVG path
    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${arcFlag} ${sweepFlag} ${endX} ${endY}`;
  };

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 158 150"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
      }}
    >
      <circle
        cx="79"
        cy="75"
        r="58"
        fill={isDarkMode ? "#2A2E39" : "#F1F1F1"}
      />

      {/* Background track circle */}
      <circle
        cx="79"
        cy="75"
        r="58"
        stroke={
          isDarkMode ? "rgba(58, 63, 75, 0.4)" : "rgba(225, 225, 225, 0.7)"
        }
        strokeWidth="12"
        fill="none"
      />

      {/* Score arc */}
      <path
        d={calculateArc(score)}
        stroke={getScoreColor(score)}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        style={{
          transform: "rotate(180deg)",
          transformOrigin: "center",
        }}
      />

      {/* Center circle */}
      <g>
        <circle
          cx="79"
          cy="75"
          r="48"
          fill={isDarkMode ? "#2A2E39" : "white"}
        />
        <circle
          cx="79"
          cy="75"
          r="47.5"
          stroke={isDarkMode ? "#3A3F4B" : "white"}
          strokeWidth="1.04167"
        />
      </g>
      <g>
        <circle
          cx="79"
          cy="75"
          r="42"
          fill={isDarkMode ? "#353A47" : "#F3F5FF"}
        />
        <circle
          cx="79"
          cy="75"
          r="41.5"
          stroke={isDarkMode ? "#3A3F4B" : "white"}
          strokeWidth="1.04167"
        />
      </g>
      <text
        x="79"
        y="75"
        dominantBaseline="middle"
        textAnchor="middle"
        fill={isDarkMode ? "#FFFFFF" : "#000000"}
        fontSize="32"
        fontWeight="bold"
        style={{ userSelect: "none" }}
      >
        {score}
      </text>
      <text
        x="79"
        y="95"
        dominantBaseline="middle"
        textAnchor="middle"
        fill={getScoreColor(score)}
        fontSize="14"
        style={{ userSelect: "none" }}
      >
        {getScoreLabel(score)}
      </text>
      <defs></defs>
    </svg>
  );
};

const IntelliScore: React.FC = () => {
  const getScoreColor = (score: number): string => {
    if (score >= 70) return "#4CAF50"; // Green
    if (score >= 40) return "#FF9800"; // Orange
    return "#F44336"; // Red
  };
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Chrome detection - only apply styles in Chrome, not Edge
  const [isChromeOnly, setIsChromeOnly] = useState(false);

  useEffect(() => {
    const isChrome = /Chrome/.test(navigator.userAgent) &&
      !/Edg|Edge/.test(navigator.userAgent) &&
      !/OPR|Opera/.test(navigator.userAgent);
    setIsChromeOnly(isChrome);
  }, []);
  const [downloading, setDownloading] = useState(false);
  const [scores, setScores] = useState<ScoreProps[]>([
    {
      value: 0,
      label: "Overall",
      color: getScoreColor(0),
      assets: 0,
      trend: "neutral",
    },
  ]);
  const [actualScore, setActualScore] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [initialSkeletonShown, setInitialSkeletonShown] = useState(false);
  // const [reservedHeight, setReservedHeight] = useState<number | undefined>(
  //   undefined
  // );
  const navigate = useNavigate();
  const [trigger, setTrigger] = useState(0);
  const [chartKey, setChartKey] = useState(0); // Key to force chart re-render
  // tooltip handled via MUI Tooltip hover — no explicit open state needed
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportFormat, setReportFormat] = useState("excel");
  // const { getFromLocalStorage } = useStorageManager();
  // const [isNewUserDetails, setIsNewUserDetails] = useState<any>(null);
  const isNewUserTrue = useSelector(
    (state: RootState) => state.UserLoginDetails.isNewUserTrue
  );
  const isOnlyCloudUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isOnlyCloudUser
  );
  const isOnlyPremAssetUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isOnlyPremAssetUser
  );
  const isBothCloudAndPremAssetUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isBothCloudAndPremAssetUser
  );

  console.log(isOnlyPremAssetUser, "isOnlyPremAssetUser");
  console.log(isOnlyCloudUser, "isOnlyCloudUser");
  console.log(isNewUserTrue, "isNewUserTrue");
  console.log(isBothCloudAndPremAssetUser, "isBothCloudAndPremAssetUser");

  // iPad detection - iPad mini, iPad Air, and iPad Pro in both orientations
  // iPad mini: 768px × 1024px, iPad Air: 820px × 1180px, iPad Pro 11": 834px × 1194px, iPad Pro 12.9": 1024px × 1366px
  const isIPad = useMediaQuery(
    [
      "(min-width: 768px) and (max-width: 1024px) and (orientation: portrait)", // iPad mini portrait
      "(min-width: 820px) and (max-width: 1180px) and (orientation: portrait)", // iPad Air portrait
      "(min-width: 834px) and (max-width: 1194px) and (orientation: portrait)", // iPad Pro 11" portrait
      "(min-width: 1024px) and (max-width: 1366px) and (orientation: portrait)", // iPad Pro 12.9" portrait
      "(min-width: 1024px) and (max-width: 1366px) and (orientation: landscape)", // iPad mini/Air/Pro landscape
      "(min-width: 1180px) and (max-width: 1366px) and (orientation: landscape)", // iPad Air/Pro landscape
      "(min-width: 1194px) and (max-width: 1366px) and (orientation: landscape)", // iPad Pro landscape
    ].join(", ")
  );

  // iPad Pro specific detection for larger sizing
  const isIPadPro = useMediaQuery(
    [
      "(min-width: 834px) and (max-width: 1194px) and (orientation: portrait)", // iPad Pro 11" portrait
      "(min-width: 1024px) and (max-width: 1366px) and (orientation: portrait)", // iPad Pro 12.9" portrait
      "(min-width: 1194px) and (max-width: 1366px) and (orientation: landscape)", // iPad Pro landscape
    ].join(", ")
  );

  useEffect(() => {
    // Increment chartKey to trigger re-render when isDarkMode changes
    setChartKey((prevKey) => prevKey + 1);
  }, [isDarkMode]);
  // useEffect(() => {
  //   const fetchUserDetails = async () => {
  //     let parsed = null;
  //     const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
  //     console.log("isNewUserDetails from API:", res?.data?.data);
  //     if (res.status === 200) {
  //       parsed = res?.data?.data || false;
  //     }

  //     if (parsed.device_count == 0 && parsed.cloud_count == 0) {
  //       setIsNewUserTrue(true);
  //     } else if (parsed.cloud_count > 0 && parsed.device_count == 0) {
  //       setIsOnlyCloudUser(true);
  //     } else if (parsed.device_count > 0 && parsed.cloud_count == 0) {
  //       console.log("isNewUserDetails from localStorage:", parsed);
  //       console.log("isOnlyPremAssetUser", isOnlyPremAssetUser);
  //       setIsOnlyPremAssetUser(true);
  //     } else {
  //       setIsBothCloudAndPremAssetUser(true);
  //     }
  //     console.log("isNewUserDetails from localStorage:", parsed);
  //   };

  //   fetchUserDetails();
  // }, []);

  useEffect(() => {
    const handleFilterSaved = () => {
      setInitialSkeletonShown(false);
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchIntelliScore = async () => {
      try {
        const response = await axiosInstance.get(
          END_POINTS.DASHBOARD_INTELLISCORE
        );
        const data = response.data.data;

        setScores([
          {
            value: Math.round(data.intelli_score),
            label: "Overall",
            color: getScoreColor(data.intelli_score),
            assets: data.overall_asset_count,
            trend: "neutral",
          },

          ...(isBothCloudAndPremAssetUser || isOnlyPremAssetUser
            ? [
              {
                value: Math.round(data.on_prem_score),
                label: (
                  <span
                    style={{
                      fontWeight: 900,
                    }}
                  >
                    On-prem
                  </span>
                ),
                color: getScoreColor(data.on_prem_score),
                trend: "neutral" as const,
                assets: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    {data.on_premise_asset_count}
                  </span>
                ),
                subLabel: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    Assets
                  </span>
                ),
              },
              {
                value: Math.round(data.cve_score),
                label: (
                  <span
                    style={{
                      fontWeight: 900,
                    }}
                  >
                    CVE
                  </span>
                ),
                color: getScoreColor(data.cve_score),
                trend: "neutral" as const,
                assets: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    {data.cve_asset_count}
                  </span>
                ),
                subLabel: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    Assets
                  </span>
                ),
              },
            ]
            : []),
          // Only add the Cloud score if isOnlyPremAssetUser is false
          ...(!isOnlyPremAssetUser
            ? [
              {
                value: Math.round(data.cloud_score),
                label: (
                  <span
                    style={{
                      fontWeight: 900,
                    }}
                  >
                    Cloud
                  </span>
                ),
                color: getScoreColor(data.cloud_score),
                trend: "neutral" as const,
                assets: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    {data.cloud_asset_count}
                  </span>
                ),
                subLabel: (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                    }}
                  >
                    Resources
                  </span>
                ),
              },
            ]
            : []),
        ]);
        setActualScore(data.actual_score ?? 0);
      } catch (error) {
        console.error("Error fetching IntelliScore:", error);
      } finally {
        setLoading(false);
        setInitialSkeletonShown(true);
      }
    };

    fetchIntelliScore();
  }, [
    trigger,
    isBothCloudAndPremAssetUser,
    isOnlyPremAssetUser,
    isOnlyCloudUser,
  ]);

  // Download dialog handlers
  const handleDownloadClick = () => {
    setIsReportDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsReportDialogOpen(false);
  };

  const handleDownloadReport = () => {

    setDownloading(true);
    const payload = {
      report_format: reportFormat.toLowerCase(),

      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type: "org_summary",
      asset_ids: "1",
        // report_name:"Organisation Summary Report",
        // module:"on_premise",
        // is_scheduled:false,
        // frequenct_name:"",
        // description:"",
        // customise_asset_ids:[],
        // report_metadata: {
        //   asset_compliance: {
        //     compliance_description: false,
        //     frameworks: ["NIST_800-171 r2", "NIST_800-53 r5", "CIS_8", "PCI DSS_4.0.1","NIST_800-53 r4", "CIS_7.1", "SPARK_1"]
        //   }
        // }
      //organization_id: organization_id,
    };
    console.log(payload, 'payload');

    axiosInstance.post(END_POINTS.REPORT_DOWNLOAD, payload)
      .then(response => {
        if (response.status === 200) {
          toast.success("Report request submitted, please check Reports section.");
        }
        setIsReportDialogOpen(false);
        //saveFile(response.data, 'AssetsComplianceReport');
        //onClose();
      })
      .catch(error => {
        console.error('Error making API call:', error);
        toast.error("Failed to submit report request, please try again.");
      })
      .finally(() => {
        //setDownloadLoading(false);
        setIsReportDialogOpen(false);
        setDownloading(false);
      });

  };

  {
  }
  if (loading && !initialSkeletonShown) {
    if (isBothCloudAndPremAssetUser) {
      return (
        //write if condition

        <Box height="100%">
          <Paper
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor:
                theme.palette.mode === "dark" ? "background.paper" : "white",
              // boxShadow:
              //   theme.palette.mode === "dark"
              //     ? "0px 4px 20px rgba(0, 0, 0, 0.3)"
              //     : "0px 4px 20px rgba(0, 0, 0, 0.05)",
              borderRadius: 0,
            }}
          >
            <Box className={styles.header} sx={{ mb: 1 }}>
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    height: 200,
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={180}
                    height={180}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.3 },
                        "50%": { opacity: 0.6 },
                        "100%": { opacity: 0.3 },
                      },
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={160}
                    height={160}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.2s",
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={140}
                    height={140}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.4s",
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={120}
                    height={120}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.6s",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={7} md={8}>
                <Grid container spacing={1}>
                  {[1, 2, 3].map((index) => (
                    <Grid item xs={4} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                          height: 120,
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          width={100}
                          height={100}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${0.2 * index}s`,
                          }}
                        />
                        <Skeleton
                          variant="circular"
                          width={80}
                          height={80}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${0.2 * index + 0.2}s`,
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: 1, textAlign: "center" }}>
                        <Skeleton
                          variant="text"
                          width={60}
                          height={24}
                          sx={{ mx: "auto" }}
                        />
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ mx: "auto" }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      );
    }

    if (isOnlyCloudUser) {
      return (
        //write if condition

        <Box height="100%">
          <Paper
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              bgcolor:
                theme.palette.mode === "dark" ? "background.paper" : "white",
              // boxShadow:
              //   theme.palette.mode === "dark"
              //     ? "0px 4px 20px rgba(0, 0, 0, 0.3)"
              //     : "0px 4px 20px rgba(0, 0, 0, 0.05)",
              borderRadius: 0,
            }}
          >
            <Box
              className={styles.header}
              sx={{
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    height: 120,
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={180}
                    height={180}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "150%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.3 },
                        "50%": { opacity: 0.6 },
                        "100%": { opacity: 0.3 },
                      },
                    }}
                  />
                  {/* <Skeleton
                  variant="circular"
                  width={160}
                  height={160}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    animation: "pulse 1.5s ease-in-out infinite",
                    animationDelay: "0.2s",
                  }}
                />
                <Skeleton
                  variant="circular"
                  width={140}
                  height={140}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    animation: "pulse 1.5s ease-in-out infinite",
                    animationDelay: "0.4s",
                  }}
                />
                <Skeleton
                  variant="circular"
                  width={120}
                  height={120}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    animation: "pulse 1.5s ease-in-out infinite",
                    animationDelay: "0.6s",
                  }}
                /> */}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      );
    }

    if (isOnlyPremAssetUser) {
      return (
        //write if condition

        <Box height="100%">
          <Paper
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor:
                theme.palette.mode === "dark" ? "background.paper" : "white",
              // boxShadow:
              //   theme.palette.mode === "dark"
              //     ? "0px 4px 20px rgba(0, 0, 0, 0.3)"
              //     : "0px 4px 20px rgba(0, 0, 0, 0.05)",
              borderRadius: 0,
            }}
          >
            <Box className={styles.header} sx={{ mb: 1 }}>
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    height: 120,
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={180}
                    height={180}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.3 },
                        "50%": { opacity: 0.6 },
                        "100%": { opacity: 0.3 },
                      },
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={160}
                    height={160}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.2s",
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={140}
                    height={140}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.4s",
                    }}
                  />
                  <Skeleton
                    variant="circular"
                    width={120}
                    height={120}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: "pulse 1.5s ease-in-out infinite",
                      animationDelay: "0.6s",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={7} md={8}>
                <Grid container spacing={2}>
                  {[1, 2].map((index) => (
                    <Grid item xs={6} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                          height: 120,
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          width={100}
                          height={100}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${0.2 * index}s`,
                          }}
                        />
                        <Skeleton
                          variant="circular"
                          width={80}
                          height={80}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${0.2 * index + 0.2}s`,
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: 1, textAlign: "center" }}>
                        <Skeleton
                          variant="text"
                          width={60}
                          height={24}
                          sx={{ mx: "auto" }}
                        />
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ mx: "auto" }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      );
    }
  }

  function getLabelText(label: React.ReactNode): string {
    if (typeof label === "string" || typeof label === "number")
      return String(label);
    if (Array.isArray(label)) return label.map(getLabelText).join("");
    if (React.isValidElement(label)) {
      const props = label.props as { children?: React.ReactNode };
      return getLabelText(props.children);
    }
    return "";
  }

  // Options for the smaller charts (On-prem, Cloud, CVE) - Applying gradient styling
  const smallChartOptions = (score: ScoreProps): ApexCharts.ApexOptions => ({
    chart: {
      id: "intelliscore-chart",
      height: 120, // Smaller height
      type: "radialBar",
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    plotOptions: {
      radialBar: {
        // Apply hollow styling from large chart
        hollow: {
          margin: 0,
          size: "65%", // Adjusted size for smaller chart
          background: theme.palette.background.paper,
          image: undefined,
          position: "front",
          dropShadow: {
            enabled: true,
            top: 1, // Adjusted shadow for smaller size
            left: 0,
            blur: 2,
            opacity: 0.1,
          },
        },
        // Apply track styling from large chart
        track: {
          background:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "#e7e7e7",
          strokeWidth: "75%", // Adjusted width for smaller size
          margin: 0,
          dropShadow: {
            enabled: true,
            top: -1, // Adjusted shadow for smaller size
            left: 0,
            blur: 2,
            opacity: 0.15,
          },
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            offsetY: 8,
            fontSize: "22px",
            fontWeight: 600,
            color: theme.palette.text.primary,
            formatter: function (val) {
              return val.toString();
            },
          },
        },
      },
    },
    // Apply fill styling from large chart
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "horizontal",
        shadeIntensity: 0.5,
        // Use score color for gradient
        gradientToColors: [getScoreColor(score.value)],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.8,
        stops: [0, 100],
      },
    },
    // Apply stroke styling from large chart
    stroke: {
      lineCap: "round",
    },
    // Use score color for the bar itself, consistent with gradient
    colors: [getScoreColor(score.value)],
    series: [score.value],
    labels: [""], // No label inside the chart
  });

  const renderScoreChart = (score: ScoreProps, isLarge: boolean) => {
    if (isLarge) {
      return (
        <Box
          className={styles.scoreCard}
          sx={{
            textAlign: "center",
            padding: 1,
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: isChromeOnly ? "210px" : 0,
            minWidth: isChromeOnly ? "210px" : 0,
            maxWidth: isChromeOnly ? "210px" : "100%",
            maxHeight: isChromeOnly ? "210px" : "100%",
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: isChromeOnly ? "150px" : "200px",
              maxHeight: isChromeOnly ? "150px" : "200px",
              minWidth: isChromeOnly ? "150px" : undefined,
              minHeight: isChromeOnly ? "150px" : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              flexShrink: 0,
            }}
          >
            <MainProgressGauge
              score={score.value}
            />
          </Box>
        </Box>
      );
    }

    const labelText = getLabelText(score.label);
    // Handle all small widgets (On-prem, Cloud, CVE) the same way
    if (["On-prem", "Cloud", "CVE"].includes(labelText)) {
      const widgetType =
        labelText === "On-prem"
          ? "onprem"
          : (labelText.toLowerCase() as "cloud" | "cve");

      return (
        <Box
          className={styles.scoreCard}
          sx={{
            textAlign: "center",
            padding: " 9px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: isOnlyCloudUser ? (isChromeOnly ? 150 : 200) : (isChromeOnly ? (isOnlyPremAssetUser ? 100 : 115) : (isOnlyPremAssetUser ? 105 : 130)),
              height: isOnlyCloudUser ? (isChromeOnly ? 150 : 200) : (isChromeOnly ? (isOnlyPremAssetUser ? 100 : 115) : (isOnlyPremAssetUser ? 105 : 130)),
            }}
          >
            <SmallIntelliScoreWidget
              score={score.value}
              type={widgetType}
              width="100%"
              height="100%"
            />
          </Box>
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {score.label}
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              my={0.5}
            >
              {score.trend !== "neutral" && (
                <Typography
                  variant="caption"
                  className={
                    score.trend === "up" ? styles.trendUp : styles.trendDown
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 0.5,
                    gap: 0.2,
                  }}
                >
                  {score.trend === "up" ? (
                    <ArrowUpwardIcon sx={{ fontSize: "inherit" }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ fontSize: "inherit" }} />
                  )}
                  {String(score.trendValue).padStart(2, "0")}
                </Typography>
              )}
              <Typography variant="caption">
                {score.subLabel && <span>{score.subLabel}:</span>} {score.assets}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    // Fallback for any other charts (shouldn't be used in current implementation)
    const options = smallChartOptions(score);
    const height = 120;

    return (
      <Box
        className={styles.scoreCard}
        sx={{
          textAlign: "center",
          padding: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <ReactApexChart
          key={chartKey}
          options={options}
          series={options.series}
          type="radialBar"
          height={height}
        />
        <Box mt={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {score.label}
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            my={0.5}
          >
            {score.trend !== "neutral" && (
              <Typography
                variant="caption"
                className={
                  score.trend === "up" ? styles.trendUp : styles.trendDown
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 0.5,
                  gap: 0.2,
                }}
              >
                {score.trend === "up" ? (
                  <ArrowUpwardIcon sx={{ fontSize: "inherit" }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: "inherit" }} />
                )}
                {String(score.trendValue).padStart(2, "0")}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {score.subLabel}: {score.assets}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box height="100%" className={isChromeOnly ? styles.chromeOnly : ''}>
      <Paper
        sx={{
          p: isOnlyCloudUser || isOnlyPremAssetUser ? 1 : 1,
          pt: isOnlyCloudUser || isOnlyPremAssetUser ? 0.5 : -2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
          // width:isbothCloudAndPremAssetUser? "100%":"70%", // Always take full width for responsiveness
          borderRadius: "4px",
        }}
      >
        <Box className={styles.header} sx={{ padding: 0 }}>
          <Typography
            variant="h6"
            className={styles.title}
            sx={{ fontSize: "14px" }}
          >
            <img
              src={theme.palette.mode === "dark" ? darklogo : lightlogo}
              alt="IntelliScore"
              style={{
                height: "40px",
                verticalAlign: "middle",
                fontSize: "24px",
                fontWeight: "600",
                marginTop: "-3px",
              }}
            />
          </Typography>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              color: "#696595",
            }}
          >
            <div className="no-drag clickable-area" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={handleDownloadClick}
                className="no-drag clickable-area"
              >
                <DownloadIcon
                  sx={{
                    fontSize: 20,
                  }}
                />
              </IconButton>

              <Tooltip
                title={
                  <Typography
                    variant="body2"
                    color={isDarkMode ? "text.primary" : "text.secondary"}
                    sx={{ fontSize: "0.875rem", textAlign: "center" }}
                  >
                    <strong>
                      The actual score doesn't consider any risks that are accepted
                    </strong>
                  </Typography>
                }
                placement="bottom"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: isDarkMode ? "#393E4B" : "#FFFFFF",
                      color: isDarkMode ? "text.primary" : "text.secondary",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                      fontSize: "0.875rem",
                      maxWidth: 300,
                      p: 2,
                      borderRadius: 2,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    cursor: "pointer",
                    color: isDarkMode ? "#fff" : "#696595",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(105, 101, 149, 0.1)",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" sx={{ mt: "-3px" }} />
                </IconButton>
              </Tooltip>
            </div>
            <Typography
              variant="body2"
              className={styles.actualScore}
              sx={{ fontWeight: 600 }}
            >
              Actual Score:{" "}
              <span
                className={styles.scoreHighlight}
                style={{
                  color: getScoreColor(actualScore),
                  fontWeight: 600,
                }}
              >
                {Math.round(actualScore)}
              </span>
            </Typography>
          </div>
        </Box>
        <Divider sx={{ mb: isOnlyCloudUser || isOnlyPremAssetUser ? 1 : 2 }} />
        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
          }}
        >
          {/* Mobile and Small Tablet Layout */}
          <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
            <>
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  mb: isOnlyCloudUser || isOnlyPremAssetUser ? 1 : 2,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {renderScoreChart(scores[0], true)}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "row",
                  gap: isOnlyCloudUser || isOnlyPremAssetUser ? 3 : 5,
                  alignItems: "stretch",
                  justifyContent: "center",
                }}
              >
                {scores.slice(1).map((score, index) => {
                  const labelText = getLabelText(score.label);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={
                        isBothCloudAndPremAssetUser
                          ? 2.5
                          : isOnlyPremAssetUser
                            ? 3.2
                            : isOnlyCloudUser
                              ? 12
                              : 4
                      }
                      key={labelText || index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        maxWidth: "100%",
                      }}
                    >
                      <Box
                        className="no-drag clickable-area"
                        sx={{
                          height: "100%",
                          width: "100%",
                          maxWidth: isOnlyCloudUser ? 250 : 220,
                          position: "relative",
                          cursor: ["CVE", "On-prem", "Cloud"].includes(
                            labelText
                          )
                            ? "pointer"
                            : "default",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                        }}
                        onClick={() => {
                          if (labelText === "CVE") {
                            navigate("/on-premises/cve");
                          } else if (labelText === "On-prem") {
                            navigate("/on-premises/assets");
                          } else if (labelText === "Cloud") {
                            navigate("/cloud/cloud-resources");
                          }
                        }}
                      >
                        {renderScoreChart(score, false)}
                      </Box>
                    </Grid>
                  );
                })}
              </Box>
            </>
          </Box>

          {/* Medium Screen Layout */}
          <Box
            sx={{
              display: { xs: "none", sm: "none", md: "block", lg: "none" },
            }}
          >
            {isOnlyPremAssetUser ? (
              // Special layout for isOnlyPremAssetUser - two rows
              <Grid
                container
                spacing={isOnlyPremAssetUser ? 2 : 3}
                sx={{ height: "100%" }}
              >
                {/* First row - Main Score Chart */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50%",
                      minHeight: isOnlyPremAssetUser ? "180px" : "200px",
                    }}
                  >
                    {renderScoreChart(scores[0], true)}
                  </Box>
                </Grid>

                {/* Second row - Two smaller charts */}
                <Grid item xs={12}>
                  <Grid
                    container
                    spacing={isOnlyPremAssetUser ? 2 : 4}
                    sx={{
                      height: "50%",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {scores.slice(1).map((score, index) => {
                      const labelText = getLabelText(score.label);
                      return (
                        <Grid
                          item
                          xs={5}
                          key={labelText || index}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Box
                            className="no-drag clickable-area"
                            sx={{
                              height: "100%",
                              width: "100%",
                              position: "relative",
                              cursor: ["CVE", "On-prem", "Cloud"].includes(
                                labelText
                              )
                                ? "pointer"
                                : "default",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => {
                              if (labelText === "CVE") {
                                navigate("/on-premises/cve");
                              } else if (labelText === "On-prem") {
                                navigate("/on-premises/assets");
                              } else if (labelText === "Cloud") {
                                navigate("/cloud/cloud-resources");
                              }
                            }}
                          >
                            {renderScoreChart(score, false)}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              // Original layout for other user types
              <Grid container spacing={2} sx={{ height: "100%" }}>
                {/* Main Score Chart */}
                <Grid item xs={12} sm={5} md={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    {renderScoreChart(scores[0], true)}
                  </Box>
                </Grid>

                {/* Smaller Score Charts */}
                <Grid item xs={12} sm={7} md={8}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      height: "100%",
                      justifyContent: "flex-start",
                    }}
                  >
                    {scores.slice(1).map((score, index) => {
                      const labelText = getLabelText(score.label);
                      return (
                        <Grid
                          item
                          xs={4}
                          key={labelText || index}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Box
                            className="no-drag clickable-area"
                            sx={{
                              height: "100%",
                              width: "100%",
                              position: "relative",
                              cursor: ["CVE", "On-prem", "Cloud"].includes(
                                labelText
                              )
                                ? "pointer"
                                : "default",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => {
                              if (labelText === "CVE") {
                                navigate("/on-premises/cve");
                              } else if (labelText === "On-prem") {
                                navigate("/on-premises/assets");
                              } else if (labelText === "Cloud") {
                                navigate("/cloud/cloud-resources");
                              }
                            }}
                          >
                            {renderScoreChart(score, false)}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Desktop Layout */}
          <Box sx={{ display: { xs: "none", sm: "none", lg: "block" } }}>
            {isOnlyCloudUser ? (
              /* ====== Cloud-Only Desktop Layout (dedicated) ====== */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  height: "100%",
                  gap: 3,
                  mt: 2,
                }}
              >
                {/* Main Score Gauge */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    flexShrink: 0,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: "-12px",
                      top: "10%",
                      bottom: "10%",
                      width: "1px",
                      backgroundColor: theme.palette.divider,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: isChromeOnly ? "150px" : "200px",
                      height: isChromeOnly ? "150px" : "200px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MainProgressGauge score={scores[0].value} />
                  </Box>
                </Box>

                {/* Cloud Score Widget */}
                <Box
                  className="no-drag clickable-area"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/cloud/cloud-resources")}
                >
                  <Box
                    sx={{
                      width: isChromeOnly ? 115 : 130,
                      height: isChromeOnly ? 115 : 130,
                    }}
                  >
                    <SmallIntelliScoreWidget
                      score={scores.length > 1 ? scores[1].value : 0}
                      type="cloud"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                  <Box mt={1} sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {scores.length > 1 ? scores[1].label : "Cloud"}
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      my={0.5}
                    >
                      {scores.length > 1 && scores[1].trend !== "neutral" && (
                        <Typography
                          variant="caption"
                          className={
                            scores[1].trend === "up" ? styles.trendUp : styles.trendDown
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mr: 0.5,
                            gap: 0.2,
                          }}
                        >
                          {scores[1].trend === "up" ? (
                            <ArrowUpwardIcon sx={{ fontSize: "inherit" }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: "inherit" }} />
                          )}
                          {String(scores[1].trendValue).padStart(2, "0")}
                        </Typography>
                      )}
                      <Typography variant="caption">
                        {scores.length > 1 && scores[1].subLabel && (
                          <span>{scores[1].subLabel}:</span>
                        )}{" "}
                        {scores.length > 1 ? scores[1].assets : 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              /* ====== Default Desktop Layout (on-prem / both / hybrid) ====== */
              <Grid
                container
                spacing={isOnlyPremAssetUser ? 2 : 3}
                sx={{ height: "100%", alignItems: "center" }}
              >
                {/* Main Score Chart */}
                <Grid item xs={12} lg={4} xl={3}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: "-5px",
                        top: "10%",
                        bottom: "10%",
                        width: "1px",
                        backgroundColor: theme.palette.divider,
                      },
                    }}
                  >
                    {renderScoreChart(scores[0], true)}
                  </Box>
                </Grid>

                {/* Smaller Score Charts */}
                <Grid item xs={12} lg={8} xl={9}>
                  <Grid
                    container
                    spacing={isOnlyPremAssetUser ? 4 : 3}
                    sx={{
                      height: "100%",
                      justifyContent: isOnlyPremAssetUser
                        ? "space-around"
                        : "flex-start",
                    }}
                  >
                    {scores.slice(1).map((score, index) => {
                      const labelText = getLabelText(score.label);
                      return (
                        <Grid
                          item
                          xs={12}
                          sm={isOnlyPremAssetUser ? 5 : 4}
                          lg={isOnlyPremAssetUser ? 5 : 4}
                          xl={isOnlyPremAssetUser ? 5 : 4}
                          key={labelText || index}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Box
                            className="no-drag clickable-area"
                            sx={{
                              height: "100%",
                              width: "100%",
                              position: "relative",
                              borderLeft:
                                index > 0 && !isOnlyPremAssetUser
                                  ? `1px solid ${theme.palette.divider}`
                                  : "none",
                              pl: index > 0 && !isOnlyPremAssetUser ? 1 : 0,
                              cursor: ["CVE", "On-prem", "Cloud"].includes(
                                labelText
                              )
                                ? "pointer"
                                : "default",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              "&::after":
                                isOnlyPremAssetUser &&
                                  index < scores.slice(1).length - 1
                                  ? {
                                    content: '""',
                                    position: "absolute",
                                    right: "-40px",
                                    top: "0",
                                    bottom: "0",
                                    width: "1px",
                                    backgroundColor: theme.palette.divider,
                                    zIndex: 1,
                                    transform: "translateY(15%)",
                                  }
                                  : {},
                            }}
                            onClick={() => {
                              if (labelText === "CVE") {
                                navigate("/on-premises/cve");
                              } else if (labelText === "On-prem") {
                                navigate("/on-premises/assets");
                              } else if (labelText === "Cloud") {
                                navigate("/cloud/cloud-resources");
                              }
                            }}
                          >
                            {renderScoreChart(score, false)}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Download Report Dialog */}
      <Dialog
        open={isReportDialogOpen}
        onClose={handleDialogClose}
        sx={{
          "& .MuiDialog-paper": {
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "360px",
            height: "300px",
            borderRadius: "4px",
            transform: "translate(-50%, -50%)",
            padding: "10px",
            boxSizing: "border-box",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            color: isDarkMode ? "#ffffff" : "#101828",
          }}
        >
          Download Org Summary Report?
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            fontWeight: 400,
            fontSize: "14px",
            color: isDarkMode ? "#b0b0b0" : "#667085",
            overflow: "visible",
            maxHeight: "none",
          }}
        >
          <Typography variant="body2" sx={{ mb: 2, color: isDarkMode ? "#b0b0b0" : "#666666" }}>
            This report offers a comprehensive summary of your Organization data
          </Typography>
          <Typography
            variant="body2"
            fontWeight={500}
            align="center"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Report Format
          </Typography>
          <RadioGroup
            value={reportFormat}
            onChange={(e) => setReportFormat(e.target.value)}
            row
            sx={{ justifyContent: "center" }}
          >

            <FormControlLabel value="excel" control={<Radio />} label="Excel" />
            <FormControlLabel value="pdf" control={<Radio />} label="Pdf" />

          </RadioGroup>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            paddingBottom: "24px",
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{ width: 130 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadReport}
            variant="contained"
            disabled={downloading}
            color="primary"
            sx={{ width: 130 }}
          >
            {downloading ? (<Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={10} sx={{ color: 'white' }} />
              <Typography sx={{ color: 'white', fontSize: '11px' }}>
                Downloading...
              </Typography>
            </Box>
            ) : (
              "Download"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntelliScore;
